import { boundMethod } from 'autobind-decorator';
import type { AsyncStatus } from 'lib-shared-legacy-types';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';


import { getAvailableCountries } from './api/countries';
import type { AvailableCountriesGetItemsResponse, Country } from './api/types';

export interface WithAvailableCountriesInjectedProps {
  availableCountries: WithAvailableCountriesState &
    WithAvailableCountriesMethods;
}

export interface WithAvailableCountriesState {
  items: Array<Country>;
  getItemsAsyncStatus: AsyncStatus;
}

export interface WithAvailableCountriesMethods {
  getItems: () => Promise<AvailableCountriesGetItemsResponse>;
}

type WithAvailableCountriesNotInjectedProps<T> = Omit<
  T,
  keyof WithAvailableCountriesInjectedProps
>;

export const withAvailableCountries = <
  T extends WithAvailableCountriesInjectedProps,
>(
  Component: ComponentType<T>,
) => {
  class WithAvailableCountries
    extends PureComponent<
      WithAvailableCountriesNotInjectedProps<T>,
      WithAvailableCountriesState
    >
    implements WithAvailableCountriesMethods
  {
    state = {
      items: [],
      getItemsAsyncStatus: 'success' as AsyncStatus,
    };

    memoizedGetInjectedProps = memoize(this.getInjectedProps).bind(this);

    @boundMethod
    getItems() {
      this.setState({ getItemsAsyncStatus: 'loading' });

      return getAvailableCountries().then(
        this.handleQueryResolve,
        this.handleQueryReject,
      );
    }

    @boundMethod
    handleQueryResolve(response: AvailableCountriesGetItemsResponse) {
      const {
        data: { countries },
      } = response;
      const items = countries.sort((a, b) =>
        a.country.localeCompare(b.country),
      );

      this.setState({
        items,
        getItemsAsyncStatus: 'succeeded',
      });

      return response;
    }

    @boundMethod
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handleQueryReject(error: any) {
      this.setState({ getItemsAsyncStatus: 'failed' });

      return error;
    }

    getInjectedProps(
      items: Readonly<WithAvailableCountriesState>['items'],
      getItemsAsyncStatus: Readonly<WithAvailableCountriesState>['getItemsAsyncStatus'],
    ): WithAvailableCountriesInjectedProps['availableCountries'] {
      return {
        items,
        getItemsAsyncStatus,
        getItems: this.getItems,
      };
    }

    render() {
      const { items, getItemsAsyncStatus } = this.state;

      return (
        <Component
          {...(this.props as T)}
          availableCountries={this.memoizedGetInjectedProps(
            items,
            getItemsAsyncStatus,
          )}
        />
      );
    }
  }

  return WithAvailableCountries;
};
